import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, useMatch, useNavigate } from "@tanstack/react-router";
import {
	ArrowRight,
	Clock,
	Heart,
	Lightbulb,
	Loader2,
	MessageCircle,
	Pause,
	SkipForward,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { apiService } from "@/lib/api-service";

export const Route = createFileRoute("/_app/_/interview")({
	component: RouteComponent,
});

export default function RouteComponent() {
	// Check if we're at exactly /interview or at a child route like /interview/session
	const isExactMatch = useMatch({ from: "/_app/_/interview", shouldThrow: false });
	const isAtExactPath = isExactMatch && !window.location.pathname.includes("/interview/session");

	// If at a child route, render the Outlet for the child
	if (!isAtExactPath) {
		return <Outlet />;
	}

	return <InterviewIntroPage />;
}

function InterviewIntroPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const skipMutation = useMutation({
		mutationFn: () => apiService.skipInterviewAndGenerate(),
		onSuccess: () => {
			toast.success("Interview skipped. Strategy generation started.");
			queryClient.invalidateQueries({ queryKey: ["strategy-status"] });
			navigate({ to: "/strategy" });
		},
		onError: (error: Error) => {
			toast.error(error.message || "Failed to skip interview.");
		},
	});

	const tips = [
		{
			icon: Clock,
			title: "Take your time",
			description:
				"This conversation takes about 30 minutes. There's no rush — thoughtful answers lead to better strategy.",
		},
		{
			icon: Lightbulb,
			title: "Be honest & detailed",
			description:
				"The more context you share, the more personalized and effective your campaign plan will be.",
		},
		{
			icon: Pause,
			title: "Pause anytime",
			description:
				"Need a break? No problem. The interview will pick up exactly where you left off.",
		},
		{
			icon: Heart,
			title: "Share your story",
			description:
				"Your unique experiences and motivations are what connect you to voters. Don't hold back.",
		},
	];

	return (
		<div className="h-full bg-background flex flex-col items-center justify-center px-6 py-4 md:py-8">
			<div className="w-full max-w-lg space-y-4">
				{/* Header */}
				<div className="text-center space-y-3">
					<div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10">
						<MessageCircle className="h-3.5 w-3.5 text-primary" />
						<span className="text-xs font-medium text-primary">
							AI Strategy Session
						</span>
					</div>
					<h1 className="text-2xl font-semibold text-foreground">
						Let's build your winning strategy
					</h1>
					<p className="text-muted-foreground">
						Xander, your AI campaign strategist, will ask you questions about
						your background, goals, and vision. This conversation shapes your
						personalized campaign plan.
					</p>
				</div>

				{/* Tips */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
					{tips.map((tip) => (
						<Card key={tip.title} className="border-border/50">
							<CardContent className="p-3">
								<div className="flex items-start gap-4">
									<div className="shrink-0 h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
										<tip.icon className="h-5 w-5 text-primary" />
									</div>
									<div className="space-y-0.5">
										<h3 className="font-medium text-foreground">
											{tip.title}
										</h3>
										<p className="text-sm text-muted-foreground">
											{tip.description}
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
					))}
				</div>

				{/* CTA */}
				<div className="space-y-3 pt-4">
					<Link to="/interview/session">
						<Button size="lg" className="w-full h-12">
							Start Conversation
							<ArrowRight className="ml-2 h-4 w-4" />
						</Button>
					</Link>
					<p className="text-xs text-center text-muted-foreground">
						You can pause and resume at any time
					</p>
					{import.meta.env.DEV && (
						<Button
							variant="outline"
							size="sm"
							className="w-full text-muted-foreground"
							onClick={() => skipMutation.mutate()}
							disabled={skipMutation.isPending}
						>
							{skipMutation.isPending ? (
								<Loader2 className="size-4 animate-spin mr-2" />
							) : (
								<SkipForward className="size-4 mr-2" />
							)}
							Skip Interview (Dev)
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
